import { decodeHTML } from 'entities';
import { hasVideo } from '@/utils/has-video';
import type { parsePath } from '@/utils/parse-path';
import type { ApiResponse } from '@/utils/data/types';
import type { CustomPostEntity } from '@/types/blocks';
import type { SiteConfig } from '@/temporary-site-config';
import type { BlockContext } from '@headstartwp/core/react';
import { hasFeaturedMedia } from '@/utils/has-featured-media';
import { Monetization } from '@/utils/data/network-api/client';
import type { Post, ExtendedPostLookup } from '@/types/entities';

type ShowFallbackVideoInContentOptions = {
	siteConfig: SiteConfig;
};

/* Logic to determine if a fallback video player should be shown for a given post. */
export function showFallbackVideoInContent(
	post: Post,
	options: ShowFallbackVideoInContentOptions,
): boolean {
	const featuredFallbackPlayerEnabled =
		options.siteConfig.video.fallbackPlayer.featured !== 'none';

	if (
		// site is configured to not show fallback video in content at all
		options.siteConfig.video.fallbackPlayer.inContent === 'none' ||
		// posts with video content should not show a fallback video
		hasVideo(post) ||
		// site is configured to play fallback video in the hero, and post has no featured media,
		// so the fallback player will be in the hero and should not be shown in the post content.
		(featuredFallbackPlayerEnabled && !hasFeaturedMedia(post)) ||
		// posts flagged as direct-only monetization should not show a fallback video
		post.meta.monetization === Monetization.DirectOnly
	) {
		return false;
	}

	return true;
}

export function decodeEntities(text: string | undefined) {
	if (!text) {
		return '';
	}

	return decodeHTML(text);
}

export function getPostLookupParams(
	obj: ExtendedPostLookup,
	requestedPage?: null | number,
): ExtendedPostLookup {
	const pagination = obj.pagination || false;
	const page = pagination ? requestedPage || 1 : 1;
	const params: ExtendedPostLookup = {
		filters: obj.filters || {},
		numberOfPosts: obj.numberOfTerms || obj.numberOfPosts,
		page,
		pagination,
		postType: obj.postType,
		taxonomy: obj.taxonomy,
		terms: obj.terms,
	};

	if (
		obj.contentSets &&
		Array.isArray(obj.contentSets) &&
		obj.contentSets.length > 0
	) {
		params.contentSets = obj.contentSets;
	}

	if (obj.league) {
		params.league = obj.league;
	}

	if (obj.categories) {
		params.categories = obj.categories;
	}

	return params;
}

export function getExtendedLookupParameters(
	lookup: ExtendedPostLookup,
	requestedPage?: null | number,
): ExtendedPostLookup {
	let { curationType, posts, terms } = lookup;
	const { filters } = lookup;

	if (!('posts' in lookup)) {
		posts = terms as unknown as CustomPostEntity[];
		terms = [];

		if (lookup.curationType === 'automatic') {
			// This is a temporary fix for the automatic curation type for taxonomy
			curationType = 'manual';
		}
	}

	const params = {
		...getPostLookupParams(lookup, requestedPage),
		curationType,
		filters,
		posts,
		terms,
		title: decodeEntities(lookup.title),
	};

	if (curationType === 'manual') {
		const safePosts = posts || [];
		const isOnlyIds = safePosts.some((post) => !('date_gmt' in post));

		if (isOnlyIds) {
			params.categories = safePosts.map((p) => p.id);
			params.posts = [];
			params.curationType = 'automatic';
		}
	}

	return params;
}

export function serializePostLookupKey(
	lookup: ExtendedPostLookup,
	requestedPage?: null | number,
): string {
	const { posts, ...rest } = getExtendedLookupParameters(lookup, requestedPage);

	// Use a combination of post ids to create a unique key to avoid duplicate blocks
	const postIds = posts?.map((post) => post.id);
	const finalObject = {
		...rest,
		postIds: postIds ? postIds.sort() : '',
	};
	// Sort the keys to ensure consistent serialization
	const sortedObject = Object.fromEntries(
		Object.entries(finalObject).sort(([a], [b]) => a.localeCompare(b)),
	);

	return JSON.stringify(sortedObject);
}

export function getSerializedPosts(
	blockAttributes: unknown,
	blockContext: BlockContext | undefined,
): {
	posts: CustomPostEntity[];
	totalPages: number;
} {
	let posts: CustomPostEntity[] = [];
	let totalPages = 0;
	const parsedPath = blockContext?.parsedPath as ReturnType<typeof parsePath>;
	const uniqueBlocks = (blockContext?.uniqueBlocks || {}) as Record<
		string,
		ApiResponse<CustomPostEntity[]>
	>;
	const key = serializePostLookupKey(
		blockAttributes as unknown as ExtendedPostLookup,
		parsedPath.page,
	);

	if (blockContext && key in uniqueBlocks) {
		const data = uniqueBlocks[key];
		totalPages = Number(data.headers['x-wp-totalpages']);
		posts = data.json;
	}

	return {
		posts,
		totalPages,
	};
}
